import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, publishReplay, refCount } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserEatWell } from '../models/user-eat-well.model';
import { DateHelperService } from './date-helper.service';

@Injectable({
  providedIn: 'root',
})
export class UserEatWellService {
  error = new Subject<string>();
  slug = '/user_eat_wells';
  resourceName = 'user_eat_wells';
  weeklyTarget = {
    threshold: 1,
    days: 3,
  };
  allWorkouts?: Observable<UserEatWell[]> | null = null;
  workoutsBetween?: any;

  constructor(
    private http: HttpClient,
    private dateHelperService: DateHelperService
  ) {}

  fetchBetween(startDate: Date, endDate: Date): Observable<UserEatWell[]> {
    if (!this.workoutsBetween) {
      this.workoutsBetween = {};
    }

    if (
      !this.workoutsBetween[
        this.dateHelperService.formatDate(startDate, 'YYYY-MM-DD') +
          '_' +
          this.dateHelperService.formatDate(endDate, 'YYYY-MM-DD')
      ]
    ) {
      let searchParams = new HttpParams();

      searchParams = searchParams.append(
        'startDate',
        this.dateHelperService.formatDate(startDate, 'YYYY-MM-DD')
      );
      searchParams = searchParams.append(
        'endDate',
        this.dateHelperService.formatDate(endDate, 'YYYY-MM-DD')
      );
      this.workoutsBetween[
        this.dateHelperService.formatDate(startDate, 'YYYY-MM-DD') +
          '_' +
          this.dateHelperService.formatDate(endDate, 'YYYY-MM-DD')
      ] = this.http
        .get<any>(environment.apiUrl + this.slug, {
          params: searchParams,
          responseType: 'json',
        })
        .pipe(
          map((responseData) => {
            const returnArray: UserEatWell[] = [];
            responseData['_embedded'][this.resourceName].forEach(
              (item: any) => {
                returnArray.push(item);
              }
            );
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }
    return this.workoutsBetween[
      this.dateHelperService.formatDate(startDate, 'YYYY-MM-DD') +
        '_' +
        this.dateHelperService.formatDate(endDate, 'YYYY-MM-DD')
    ];
  }
  fetchAll(): Observable<UserEatWell[]> {
    if (!this.allWorkouts) {
      this.allWorkouts = this.http
        .get<any>(environment.apiUrl + this.slug, {
          responseType: 'json',
        })
        .pipe(
          map((responseData) => {
            const returnArray: UserEatWell[] = [];
            responseData['_embedded'][this.resourceName].forEach(
              (item: any) => {
                // parse the exercise data
                if (item.exercises) {
                  item.exercises = JSON.parse(item.exercises);
                }
                returnArray.push(item);
              }
            );
            return returnArray;
          }),
          catchError((errorRes) => {
            return throwError(errorRes);
          }),
          publishReplay(1),
          refCount()
        );
    }

    return this.allWorkouts;
  }

  clearCache() {
    this.allWorkouts = null;
    this.workoutsBetween = null;
  }

  fetch(id: number) {
    return this.http
      .get<any>(environment.apiUrl + this.slug + '/' + id, {
        responseType: 'json',
      })
      .pipe(
        map((responseData) => {
          const item = new UserEatWell(
            +responseData.user_eat_well_id,
            +responseData.user_id,
            responseData.date_recorded,
            +responseData.fruit_veg,
            +responseData.protein,
            +responseData.fat,
            +responseData.carbs,
            +responseData.hydration,
            responseData.created,
            responseData.modified
          );
          return item;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  create(
    date_recorded: string,
    fruit_veg: number,
    protein: number,
    fat: number,
    carbs: number,
    hydration: number
  ) {
    this.clearCache();
    const payload = {
      date_recorded: moment(date_recorded).format('YYYY-MM-DD'),
      fruit_veg,
      protein,
      fat,
      carbs,
      hydration,
    };
    return this.http.post<UserEatWell>(
      environment.apiUrl + this.slug,
      payload,
      {
        observe: 'response',
      }
    );
  }

  update(
    id: number,
    date_recorded: string,
    fruit_veg: number,
    protein: number,
    fat: number,
    carbs: number,
    hydration: number
  ) {
    this.clearCache();
    const payload = {
      date_recorded: moment(date_recorded).format('YYYY-MM-DD'),
      fruit_veg,
      protein,
      fat,
      carbs,
      hydration,
    };
    return this.http.patch<UserEatWell>(
      environment.apiUrl + this.slug + '/' + id,
      payload,
      {
        observe: 'response',
      }
    );
  }

  delete(id: number) {
    this.clearCache();
    return this.http.delete<{ name: string }>(
      environment.apiUrl + this.slug + '/' + id
    );
  }

  generateWeekArray(currentWeek: any, data: any) {
    const weekArray: any = [];
    currentWeek.forEach((day: any) => {
      let currentDay = {
        date: moment(day).format('YYYY-MM-DD'),
        fruit_veg: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
        hydration: 0,
        score: 0,
        hasData:0,
        entryId:0
      };
      data.forEach((item: UserEatWell) => {
        if (item.date_recorded === currentDay.date) {
          currentDay.fruit_veg = +item.fruit_veg;
          currentDay.protein = +item.protein;
          currentDay.carbs = +item.carbs;
          currentDay.fat = +item.fat;
          currentDay.hydration = +item.hydration;
          currentDay.hasData = 1;
          currentDay.entryId = item.user_eat_well_id;

          let score = 0;
          if (+item.fruit_veg > 4) {
            score++;
          }
          if (+item.protein > 2) {
            score++;
          }
          if (+item.carbs > 2) {
            score++;
          }
          if (+item.fat > 2) {
            score++;
          }
          if (+item.hydration < 4) {
            score++;
          }
          currentDay.score = score;
        }
      });
      weekArray.push(currentDay);
    });
    return weekArray;
  }

  getWeekScore(weekArray: any) {
    const weekScore = weekArray.reduce((accumulator: number, object: any) => {
      return accumulator + object.score;
    }, 0);
    return weekScore;
  }


  geterateWeekTotals(weekArray: any) {
    let weekScore = weekArray.reduce((accumulator: number, object: any) => {
      return accumulator + object.score;
    }, 0);
    
    let daysEntered = 0;
    weekArray.forEach((day: any) => {
      if(day.hasData){
        daysEntered++;
      }
    });
    return { weekScore, daysEntered };
  }

  onlyUnique(value: any, index: number, self: any) {
    return self.indexOf(value) === index;
  }

  getStats(entryData:UserEatWell[]){

      let stats:any  = {
        daysHit: {
          fruit_veg:0,
          protein:0,
          fat:0,
          carbs:0,
          hydration:0,
        }
      }


      entryData.forEach((entry:any) => {
        if(entry.fruit_veg>=5){
          stats.daysHit.fruit_veg++;
        }
        if(entry.hasData && entry.hydration<=3){
          stats.daysHit.hydration++;
        }
        if(entry.protein >=3){
          stats.daysHit.protein++;
        }
        if(entry.fat >=3){
          stats.daysHit.fat++;
        }
        if(entry.carbs >=3){
          stats.daysHit.carbs++;
        }
    });


    return stats;

  }

  isJson(str: string) {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }
}
